import { Button } from '@heroui/react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDebouncedLoader } from 'use-debounced-loader';

import { LastUsedFrame } from '@/components/LastUsedChip';
import MatrixLoader from '@/shared/components/MatrixLoader';

import { optionButtonClass, optionButtonProps, optionLabelClass } from './option-button-class';
import { type SocialBrand, socialBrandLogoClass } from './social-brand';

export type Props = {
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
  readonly className?: string;
  // Part of the public button API and passed by callers (e.g. SSO connector name); not rendered directly.
  readonly target: string;
  readonly logo: string;
  readonly name: Record<string, string>;
  /** Set only for Apple and Google; resolved by the caller via `getSocialBrand`. */
  readonly brand?: SocialBrand;
  /** A recall hint only — it neither reorders nor pre-selects. */
  readonly isLastUsed?: boolean;
  readonly onClick?: () => void;
};

const SocialLinkButton = ({
  isDisabled,
  isLoading = false,
  className,
  name,
  logo,
  brand,
  isLastUsed = false,
  onClick,
}: Props) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const localName = name[language] ?? name.en;
  const isLoadingActive = useDebouncedLoader(isLoading, 300);

  return (
    <LastUsedFrame isLastUsed={isLastUsed} className={className}>
      <Button
        {...optionButtonProps}
        className={optionButtonClass}
        isDisabled={isDisabled}
        isPending={isLoadingActive}
        onPress={onClick}
      >
        {/* The logo stays put while redirecting; swapping it for the spinner read as the
            provider disappearing. */}
        {logo && (
          <img
            src={logo}
            alt=""
            className={classNames(
              'size-5 shrink-0 object-contain',
              brand && socialBrandLogoClass[brand]
            )}
          />
        )}
        <span className={optionLabelClass}>{t('action.sign_in_with', { name: localName })}</span>
        {/* Pinned to the end edge: in the flow it pushed the centred label sideways. */}
        {isLoadingActive && (
          <span className="absolute inset-y-0 inset-e-4 flex items-center animate-in fade-in duration-(--duration-quick)">
            <MatrixLoader size="sm" />
          </span>
        )}
      </Button>
    </LastUsedFrame>
  );
};

export default SocialLinkButton;
